import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.addEventListener('change', this.#updateQuote);
    closestSection.addEventListener('input', this.#updateQuote);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#updateQuote);
    this.#updateQuote();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.removeEventListener('change', this.#updateQuote);
    closestSection.removeEventListener('input', this.#updateQuote);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#updateQuote);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #updateQuote = () => {
    const quote = this.querySelector('[data-jelo-product-quote]');
    if (!quote) return;
    const section = this.closest('.shopify-section');
    const quantityInput = section?.querySelector('form[action*="/cart/add"] input[name="quantity"]');
    const quantity = Math.max(1, Number(quantityInput?.value) || 1);
    const subtotal = Math.round(Number(quote.dataset.unitPrice) * quantity);
    const shipping = subtotal > 7000 ? 0 : 699;
    const money = new Intl.NumberFormat(document.documentElement.lang || 'es', { style: 'currency', currency: quote.dataset.currency || 'EUR' });
    const shippingOutput = quote.querySelector('[data-jelo-product-shipping]');
    const totalOutput = quote.querySelector('[data-jelo-product-total]');
    if (shippingOutput) shippingOutput.textContent = money.format(shipping / 100);
    if (totalOutput) totalOutput.textContent = money.format((subtotal + shipping) / 100);
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        const quote = this.querySelector('[data-jelo-product-quote]');
        const newQuote = newProductPrice.querySelector('[data-jelo-product-quote]');
        if (quote && newQuote) quote.replaceWith(newQuote);
        this.#updateQuote();

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
